import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { User } from "../models/User";
import { Company } from "../models/Company";
import { Button, TextField, Paper, Typography, Box, Alert, Select, MenuItem, FormControl, InputLabel, FormHelperText, FormControlLabel, Checkbox } from "@mui/material";
import { createUser, getCompanies } from "../services/userService";
import { colors, fonts, buttonStyles, radius } from "../utils/colors";

const initialUser: User = {
  userName: "",
  company: "",
  email: "",
  companyId: undefined,
  entraUser: true,
};

interface CreateUserDialogProps {
  preSelectedCompanyId?: number;
  companies?: Company[];
  onSuccess?: (createdUser?: User) => void;
}

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({ preSelectedCompanyId, companies: providedCompanies, onSuccess }) => {
  const { getAccessToken, isLoggedIn, userName } = useAuth();
  const [user, setUser] = useState<User>(initialUser);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [sendInvite, setSendInvite] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Load companies on mount
  useEffect(() => {
    if (providedCompanies !== undefined) {
      setCompanies(providedCompanies);
      setLoadingCompanies(false);
      return;
    }

    if (!isLoggedIn) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);

      try {
        const result = await getCompanies(getAccessToken);
        if (result.ok && result.data) {
          setCompanies(result.data);
        }
      } catch (err) {
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [providedCompanies, isLoggedIn, getAccessToken]);

  useEffect(() => {
    if (!preSelectedCompanyId) return;
    const selectedCompany = companies.find(c => c.companyId === preSelectedCompanyId);
    if (selectedCompany) {
      setUser(prev => ({
        ...prev,
        companyId: preSelectedCompanyId,
        company: selectedCompany.companyName || ""
      }));
    }
  }, [companies, preSelectedCompanyId]);

  const isEmailDuplicate = (email: string): boolean => {
    if (!email) return false;
    const company = companies.find(c => c.companyId === user.companyId);
    if (!company?.users) return false;
    return company.users.some(
      u => u.email?.toLowerCase() === email.toLowerCase()
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === "email") {
      setEmailError(
        isEmailDuplicate(value)
          ? "A user with this email already exists in the selected company."
          : null
      );
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setUser(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleCompanyChange = (e: any) => {
    const selectedCompanyId = Number(e.target.value);
    const selectedCompany = companies.find(c => c.companyId === selectedCompanyId);
    setUser(prev => ({
      ...prev,
      companyId: selectedCompanyId,
      company: selectedCompany?.companyName || ""
    }));
    // Re-check email duplicate for the newly selected company
    if (user.email && selectedCompany?.users) {
      const dup = selectedCompany.users.some(
        u => u.email?.toLowerCase() === user.email!.toLowerCase()
      );
      setEmailError(dup ? "A user with this email already exists in the selected company." : null);
    } else {
      setEmailError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);

    // Check for duplicate email before calling API
    if (isEmailDuplicate(user.email || "")) {
      setError("A user with this email already exists in the selected company.");
      setLoading(false);
      return;
    }

    const company = companies.find(c => c.companyId === user.companyId);
    if(!company) {
      setError("Selected company not found");
      setLoading(false);
      return;
    }
    
    const groupsToAdd: string[] = [];
    
    if (user.entraUser) {
      if (company.userGroupObjectId) {
        groupsToAdd.push(company.userGroupObjectId);
      }
    }
    if (user.entraAdmin) {
      if (company.adminGroupObjectId) {
        groupsToAdd.push(company.adminGroupObjectId);
      }
    } 
    if (user.entraReader) {
      if (company.resultGroupObjectId) {
        groupsToAdd.push(company.resultGroupObjectId);
      }
    }
    

    if (groupsToAdd.length === 0) {
      setError("No valid Entra groups found for the selected company. Please check the company configuration.");
      setLoading(false);
      return;
    }
    
    try {
      const result = await createUser(user, userName || "", groupsToAdd, getAccessToken, sendInvite);
      
      if (!result.ok) {
        setError(result.error || "Failed to create user");
        return;
      }
      
      setSuccess(true);
      setUser(initialUser);

      // Call success callback if provided, passing back the user the API
      // actually created so the caller can insert it directly without a
      // follow-up fetch (which would be vulnerable to Entra group-membership
      // propagation lag right after the invite's own group-add call).
      const createdUser = result.data?.user;
      if (onSuccess) {
        setTimeout(() => {
          onSuccess(createdUser);
        }, 1000);
      }
    } catch (err: any) {
      const errorMsg = err.message || "Failed to create user";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="body2" sx={{ mb: 3, color: colors.mediumGray, fontFamily: fonts.main }}>
        Add a new user to the system. All users will be able to access the questionnaire.
      </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(false)}>
            User invited successfully!
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                      <FormControlLabel
                        control={<Checkbox checked={!!user.entraUser} onChange={handleCheckboxChange} name="entraUser" sx={{ color: colors.teal, "&.Mui-checked": { color: colors.teal } }} />}
                        label="Entra User"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={!!user.entraAdmin} onChange={handleCheckboxChange} name="entraAdmin" sx={{ color: colors.teal, "&.Mui-checked": { color: colors.teal } }} />}
                        label="Entra Admin"
                      />
                      <FormControlLabel
                        control={<Checkbox checked={!!user.entraReader} onChange={handleCheckboxChange} name="entraReader" sx={{ color: colors.teal, "&.Mui-checked": { color: colors.teal } }} />}
                        label="Entra Reader"
                      />
                    </Box>
                      <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                          control={<Checkbox checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} sx={{ color: colors.teal, "&.Mui-checked": { color: colors.teal } }} />}
                          label="Send invite email"
                        />
                      </Box>
          <TextField
            fullWidth
            label="User Name"
            name="userName"
            value={user.userName || ""}
            onChange={handleChange}
            required
            margin="normal"
            variant="outlined"
          />
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={user.email || ""}
            onChange={handleChange}
            required
            margin="normal"
            variant="outlined"
            error={!!emailError}
            helperText={emailError}
          />
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel id="company-select-label">Company</InputLabel>
            <Select
              labelId="company-select-label"
              id="company-select"
              value={user.companyId || ""}
              label="Company"
              onChange={handleCompanyChange}
              disabled={loadingCompanies}
            >
              <MenuItem value="">
                <em>Select a company</em>
              </MenuItem>
              {companies.map(company => (
                <MenuItem key={company.companyId} value={company.companyId}>
                  {company.companyName} 
                </MenuItem>
              ))}
            </Select>
            {loadingCompanies && (
              <FormHelperText>Loading companies...</FormHelperText>
            )}
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !!emailError}
            sx={{
              mt: 3,
              ...buttonStyles.secondary,
              padding: "12px",
              fontSize: "1rem",
            }}
          >
            {loading ? "Creating..." : "Create User"}
          </Button>
        </form>
    </Box>
  );
};

export default CreateUserDialog;
